import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, CircularProgress, Divider, Popover } from '@mui/material';
import dayjs from '../../../config/dayjs';
import { getNotificationTypeMeta } from '../../../common/utils/notificationTypeMeta';
import { ROUTES } from '../../../constants/routes';
import {
  useUnreadCountQuery,
  useNotificationsQuery,
  useMarkAsRead,
  useMarkAllAsRead,
} from '../../../hooks/useNotifications';
import { AppNotification } from '../../../types/notification';

function NotificationItem({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  const isUnread = !notification.read_at;
  const typeMeta = getNotificationTypeMeta(notification.type);

  return (
    <button
      onClick={() => isUnread && onRead(notification.id)}
      style={{
        display: 'flex',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        width: '100%',
        textAlign: 'left',
        background: isUnread ? '#f0fdf4' : 'transparent',
        border: 'none',
        cursor: isUnread ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: typeMeta.color + '20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ color: typeMeta.color, fontSize: 18 }}
        >
          {typeMeta.icon}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: isUnread ? 600 : 400,
              color: '#0f172a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {notification.title}
          </span>
          {isUnread && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#10b981',
                flexShrink: 0,
              }}
            />
          )}
        </div>
        <p
          style={{
            fontSize: 12,
            color: '#64748b',
            margin: '2px 0 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {notification.message.replace(/<[^>]*>/g, '')}
        </p>
        <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'block' }}>
          {dayjs(notification.created_at).fromNow()}
        </span>
      </div>
    </button>
  );
}

export const NotificationBell = ({ iconColor = '#64748b' }: { iconColor?: string }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const { data: unreadData } = useUnreadCountQuery();
  const { data, isLoading } = useNotificationsQuery(1, 8);
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllAsRead();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = data?.data ?? [];

  return (
    <>
      <button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '0.75rem',
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          transition: 'background 0.15s',
        }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              background: '#10b981',
              color: '#fff',
              fontSize: 10,
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
            notifications
          </span>
        </Badge>
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            width: 360,
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
            Notifications
            {unreadCount > 0 && (
              <span
                style={{
                  marginLeft: 8,
                  background: '#ecfdf5',
                  color: '#059669',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 99,
                }}
              >
                {unreadCount} new
              </span>
            )}
          </span>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              disabled={isMarkingAll}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                color: '#059669',
                fontWeight: 500,
                padding: 0,
              }}
            >
              Mark all read
            </button>
          )}
        </div>

        <Divider />

        {/* List */}
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <CircularProgress size={24} sx={{ color: '#10b981' }} />
            </div>
          ) : notifications.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                color: '#94a3b8',
                fontSize: 13,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 40, display: 'block', marginBottom: 8 }}
              >
                notifications_off
              </span>
              No notifications yet
            </div>
          ) : (
            notifications.map((n, i) => (
              <div key={n.id}>
                <NotificationItem notification={n} onRead={markAsRead} />
                {i < notifications.length - 1 && <Divider />}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <Divider />
        <button
          onClick={() => {
            setAnchorEl(null);
            const target = pathname.startsWith(`/${ROUTES.DASHBOARD}`)
              ? `/${ROUTES.DASHBOARD}/${ROUTES.NOTIFICATIONS}`
              : `/${ROUTES.CABINET}/${ROUTES.NOTIFICATIONS}`;
            navigate(target);
          }}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            color: '#059669',
            fontWeight: 500,
          }}
        >
          View all notifications
        </button>
      </Popover>
    </>
  );
};
